import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Spinner, apiClient, getSupabase, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'

type OrderRow = {
  id: string
  type: string
  amount_cny_cents: number | null
  amount_coin: number | null
  status: string
  created_at: string
}

// B4-#10 a5cbV0 VIP 订单详情 drawer 用 — 扩展自 /gapi/user/vip/orders/{id}
// 2026-05-25 P3-W9 B4-#3 进一步扩展:detail drawer 完整字段集(price / purchased_at /
// expires_at / days_remaining / auto_renew / payment_provider / transaction_id /
// vip_tier_at_purchase);详 docs/api-list/user/vip-purchase.md §3
type VipOrderStatus = {
  order_id: string
  plan_id: string
  plan_name: string
  status: string
  amount_cny_cents: number
  paid_at: string | null
  next_billing_at: string | null

  // P3-W9 B4-#3 扩展字段
  price_cny_cents: number
  purchased_at: string | null
  expires_at: string | null
  days_remaining: number
  auto_renew: boolean
  payment_provider: string
  transaction_id: string
  vip_tier_at_purchase: string
}

const PROVIDER_LABEL: Record<string, string> = {
  wechat_pay: '微信支付',
  alipay: '支付宝',
  coin: '金币',
  '': '—',
}

const TIER_LABEL: Record<string, string> = {
  bronze: '青铜',
  silver: '白银',
  gold: '黄金',
  platinum: '铂金',
}

const TYPE_LABEL: Record<string, string> = {
  recharge_coin: '充值',
  vip_monthly: 'VIP 月卡',
  vip_quarterly: 'VIP 季付',
  vip_yearly: 'VIP 年付',
  vip_weekly: 'VIP 周卡',
  pack_subscribe: '资源包',
}

const STATUS_LABEL: Record<string, string> = {
  paid: '已完成',
  pending: '待支付',
  cancelled: '已取消',
  failed: '失败',
  expired: '已过期',
}

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-[#FFE4E4] text-[#FF6B6B]',
  pending: 'bg-[#FEF3C7] text-[#F59E0B]',
  cancelled: 'bg-[#F1F2F4] text-[#9CA3AF]',
  failed: 'bg-[#FEE2E2] text-[#DC2626]',
  expired: 'bg-[#F1F2F4] text-[#9CA3AF]',
}

const TIER_BG: Record<string, string> = {
  vip_yearly: 'bg-[#FEF3C7] text-[#F59E0B]',
  vip_quarterly: 'bg-[#DBEAFE] text-[#3B82F6]',
  vip_monthly: 'bg-[#FFE4E4] text-[#FF6B6B]',
  vip_weekly: 'bg-[#F1F2F4] text-[#9CA3AF]',
}

function useMyOrders(userId: string | undefined) {
  return useQuery({
    queryKey: ['order', 'mine', userId] as const,
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return []
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- order 表 RLS owner 收口(只读自己的订单)
      const { data, error } = await sb
        .from('order')
        .select('id, type, amount_cny_cents, amount_coin, status, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data ?? []) as OrderRow[]
    },
  })
}

export function OrdersPage() {
  const { user } = useCurrentUser()
  const { data, isLoading } = useMyOrders(user?.user_id)

  // B4-#10 a5cbV0 订单详情 drawer
  const [drawerOrderId, setDrawerOrderId] = useState<string | null>(null)
  const isVipType = (t: string) => t.startsWith('vip_')

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="购买记录" />

        <main data-testid="hTCtD" className="flex flex-1 flex-col gap-2 px-6 pt-3 pb-12">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner className="text-gray-400" /></div>
          ) : data && data.length > 0 ? (
            <ul className="flex flex-col">
              {data.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => isVipType(o.type) && setDrawerOrderId(o.id)}
                    className={cn(
                      'flex w-full items-center gap-3 border-b border-[#F3F4F6] py-3 text-left',
                      isVipType(o.type) ? 'active:bg-[#F6F7F8]' : 'cursor-default',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[16px]',
                        TIER_BG[o.type] ?? 'bg-[#F1F2F4] text-[#9CA3AF]',
                      )}
                    >
                      👑
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-semibold text-[#1A1A1A]">{TYPE_LABEL[o.type] ?? o.type}</p>
                      <p className="text-[11px] text-[#9CA3AF]">{o.created_at.slice(0, 16).replace('T', ' ')}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[15px] font-semibold tabular-nums text-[#FF6B6B]">
                        {o.amount_cny_cents != null
                          ? `¥${(o.amount_cny_cents / 100).toFixed(2)}`
                          : o.amount_coin != null
                            ? `${o.amount_coin.toLocaleString()} 金币`
                            : '—'}
                      </span>
                      <span
                        className={cn(
                          'rounded-md px-2 py-0.5 text-[10px] font-medium',
                          STATUS_CLASS[o.status] ?? 'bg-[#F1F2F4] text-[#9CA3AF]',
                        )}
                      >
                        {STATUS_LABEL[o.status] ?? o.status}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-12 text-center text-[13px] text-[#9CA3AF]">暂无订单</p>
          )}
        </main>

        {drawerOrderId && (
          <VipOrderDetailDrawer
            orderId={drawerOrderId}
            onClose={() => setDrawerOrderId(null)}
          />
        )}
      </MobileScreen>
    </AppLayout>
  )
}

// B4-#10 VIP 订单详情 drawer — 查 /gapi/user/vip/orders/{id},
// 展示 plan_name / amount / paid_at / next_billing_at 等扩展字段。
function VipOrderDetailDrawer({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['vip-order-status', orderId] as const,
    queryFn: () => apiClient.get<VipOrderStatus>(`/gapi/user/vip/orders/${orderId}`),
  })

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="订单详情"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-3">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>
        <header className="px-6 pb-2 text-center">
          <h2 className="text-[15px] font-semibold text-[#1A1A1A]">订单详情</h2>
        </header>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
        ) : error ? (
          <p className="px-6 py-4 text-center text-[12px] text-[#FF6B6B]">
            {error instanceof Error ? error.message : '查询失败'}
          </p>
        ) : data ? (
          <dl className="grid grid-cols-2 gap-y-3 px-6 pb-4 text-[13px]">
            <dt className="text-[#9CA3AF]">套餐</dt>
            <dd className="text-right text-[#1A1A1A]">
              {data.plan_name || data.plan_id}
              {data.vip_tier_at_purchase && TIER_LABEL[data.vip_tier_at_purchase] && (
                <span className="ml-1 text-[11px] text-[#9CA3AF]">
                  · {TIER_LABEL[data.vip_tier_at_purchase]}
                </span>
              )}
            </dd>

            <dt className="text-[#9CA3AF]">金额</dt>
            <dd className="text-right font-semibold tabular-nums text-[#FF6B6B]">
              ¥{(data.amount_cny_cents / 100).toFixed(2)}
            </dd>

            <dt className="text-[#9CA3AF]">状态</dt>
            <dd className="text-right">
              <span
                className={cn(
                  'rounded-md px-2 py-0.5 text-[11px] font-medium',
                  STATUS_CLASS[data.status] ?? 'bg-[#F1F2F4] text-[#9CA3AF]',
                )}
              >
                {STATUS_LABEL[data.status] ?? data.status}
              </span>
            </dd>

            <dt className="text-[#9CA3AF]">购买时间</dt>
            <dd className="text-right text-[#1A1A1A]">
              {data.purchased_at
                ? data.purchased_at.slice(0, 16).replace('T', ' ')
                : data.paid_at
                  ? data.paid_at.slice(0, 16).replace('T', ' ')
                  : '—'}
            </dd>

            <dt className="text-[#9CA3AF]">到期时间</dt>
            <dd className="text-right text-[#1A1A1A]">
              {data.expires_at
                ? data.expires_at.slice(0, 16).replace('T', ' ')
                : data.next_billing_at
                  ? data.next_billing_at.slice(0, 16).replace('T', ' ')
                  : '—'}
            </dd>

            <dt className="text-[#9CA3AF]">剩余天数</dt>
            <dd className="text-right tabular-nums text-[#1A1A1A]">
              {data.status === 'paid' && data.days_remaining > 0
                ? `${data.days_remaining} 天`
                : '—'}
            </dd>

            <dt className="text-[#9CA3AF]">自动续费</dt>
            <dd className="text-right text-[#1A1A1A]">
              {data.auto_renew ? '已开启' : '未开启'}
            </dd>

            <dt className="text-[#9CA3AF]">支付方式</dt>
            <dd className="text-right text-[#1A1A1A]">
              {PROVIDER_LABEL[data.payment_provider] ?? data.payment_provider}
            </dd>

            <dt className="text-[#9CA3AF]">流水号</dt>
            <dd className="text-right font-mono text-[11px] text-[#9CA3AF] break-all">
              {data.transaction_id || '—'}
            </dd>

            <dt className="text-[#9CA3AF]">订单号</dt>
            <dd className="text-right font-mono text-[11px] text-[#9CA3AF]">
              {data.order_id.slice(0, 12)}…
            </dd>
          </dl>
        ) : null}
        <div className="px-6 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-full rounded-lg bg-[#19caad] text-[14px] font-semibold text-white"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}
